import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { escapeRegExp } from 'es-toolkit';
import { cn, getTestId } from '../../utils';
import { GLOBAL_HEIGHT, GlobalSize, SelectInputOption } from '../../types';
import { Field, Listbox, ListboxOption, Portal, Transition } from '@headlessui/react';
import { IconName } from '../../icons/names';
import { Icon } from '../icons/Icon';

type BaseSelectInputAttributes = Pick<
  React.SelectHTMLAttributes<HTMLSelectElement>,
  'id' | 'name' | 'className' | 'multiple' | 'disabled' | 'required' | 'autoFocus' | 'onBlur'
>;

const SelectInputVariants = cva(
  ` flex items-center p-sm gap-md border rounded-lg outline-none transition`,
  {
    variants: {
      variant: {
        default: '!bg-background-input !border-border-base',
        white: '!bg-background-input !border-border-base'
      },
      hasError: {
        true: '!border-error-text',
        false: ''
      }
    },
    defaultVariants: {
      variant: 'default',
      hasError: false
    }
  }
);

type SelectInputVariantProps = VariantProps<typeof SelectInputVariants>;

export interface SelectInputProps extends SelectInputVariantProps, BaseSelectInputAttributes {
  className?: string;
  options: SelectInputOption[];
  buttonIcon?: IconName;
  buttonLabel?: string;
  isMulti?: boolean;
  isSearchable?: boolean;
  onChange?: (value: string | number | (string | number)[]) => void;
  onSearch?: (value: string) => void;
  UnselectedOption?: string;
  value?: string | number | (string | number)[] | null;
  height?: GlobalSize;
  testId?: string;
  /** Show the dropdown open on mount. Useful when the Select is rendered as an inline editor. */
  defaultOpen?: boolean;
  /** Accessible name for the trigger. Needed wherever no visible label is wired to it. */
  ariaLabel?: string;
  /** Ids of the elements describing the trigger, e.g. a note or an error under it. */
  ariaDescribedBy?: string;
  /** The caller narrows `options` through `onSearch`; keep their order instead of re-sorting. */
  filtersExternally?: boolean;
  /** Searchable select: shows the dropdown arrow instead of the magnifying glass. */
  hidesSearchIcon?: boolean;
  /** Searchable single select only: offers the typed text as an option, so any value can be picked. */
  allowCustomValue?: boolean;
  /** Second line of the typed-text option, e.g. "Use as typed". */
  customValueDescription?: string;
  /** With `allowCustomValue`: leaving the field picks the typed text, as Enter would. */
  commitTypedOnBlur?: boolean;
}

/**
 * @param {string} [className]
 * @param {boolean} [isSearchable] Whether the select input allows searching.
 * @param {boolean} [isMulti] Whether the select input allows multi-select.
 * @param {SelectInputOption[]} options The available options for the select, required.
 * @param {(value: string | string[]) => void} [onChange] Lets you expose the selected values. Note that only the option.value will be passed as value not the entire option object!
 * @param {(value: string) => void} [onSearch] Lets you expose the searched term
 * @param {SelectInputOption | SelectInputOption[]} [value] lets you input a value.
 * @param {string} [UnselectedOption] Creates an extra options that has an undefined value, this param only provides the label
 * @param {boolean} [filtersExternally] Keeps the order of `options` the caller already filtered via onSearch
 * @param {boolean} [allowCustomValue] Offers the typed text as an option of its own
 */
export const Select = React.forwardRef<HTMLSelectElement, SelectInputProps>(
  (
    {
      className,
      isSearchable,
      hidesSearchIcon = false,
      options,
      isMulti,
      value,
      disabled,
      onChange,
      onSearch,
      onBlur,
      buttonLabel,
      buttonIcon,
      hasError = false,
      UnselectedOption,
      variant,
      height = 'md',
      testId,
      id,
      name,
      defaultOpen = false,
      ariaLabel,
      ariaDescribedBy,
      filtersExternally = false,
      allowCustomValue = false,
      customValueDescription,
      commitTypedOnBlur = false,
      autoFocus = false,
      ...props
    },
    ref
  ) => {
    // A value outside `options` is shown as typed when custom values are allowed.
    const customOption = React.useCallback(
      (candidate: unknown): SelectInputOption | undefined =>
        allowCustomValue && !isMulti && typeof candidate === 'string' && candidate.trim() !== ''
          ? { value: candidate, label: candidate }
          : undefined,
      [allowCustomValue, isMulti]
    );

    //In case the user puts a default value, its checked if values are in the options or if a disbaled options is inititally selected

    const mergedOptions = React.useMemo(() => {
      // Stable two-group sort: preferred options bubble to the top while
      // preserving their input order. The UnselectedOption (when present) is
      // always the very first row, regardless of grouping.
      const sorted = [...options.filter(o => o.preferred), ...options.filter(o => !o.preferred)];
      if (UnselectedOption && !isMulti) {
        return [{ value: '', label: UnselectedOption }, ...sorted];
      }
      return sorted;
    }, [UnselectedOption, options, isMulti]);

    const initialSelected = React.useMemo(() => {
      const list = isMulti ? options : mergedOptions;

      if (isMulti) {
        if (Array.isArray(value)) {
          return list.filter(option => value.includes(option.value));
        }
        return [];
      } else {
        const selected = list.find(option => option.value === value) ?? customOption(value);
        return selected ? [selected] : [];
      }
    }, [value, options, mergedOptions, isMulti, customOption]);

    const [selectedValues, setSelectedValues] =
      React.useState<SelectInputOption[]>(initialSelected);
    const [inputValue, setInputValue] = React.useState(''); // raw input
    const [query, setQuery] = React.useState(''); // debounced value
    const [open, isOpen] = React.useState<boolean>(defaultOpen);
    const [highlightedIndex, setHighlightedIndex] = React.useState<number>(-1);
    const [dropdownPosition, setDropdownPosition] = React.useState<{
      top?: number;
      bottom?: number;
      left: number;
      width: number;
    } | null>(null);
    const inputRef = React.useRef<HTMLInputElement>(null);
    const containerRef = React.useRef<HTMLDivElement>(null);
    const buttonRef = React.useRef<HTMLDivElement>(null);
    const dropdownRef = React.useRef<HTMLDivElement>(null);
    const highlightedOptionRef = React.useRef<HTMLElement | null>(null);
    // Picks the typed text on leaving the field, under `commitTypedOnBlur`; set below each render.
    const commitTypedRef = React.useRef<() => void>(() => undefined);

    // Anchor the menu below the trigger, or flip above when it wouldn't fit
    // below — keeps the options on-screen near the viewport bottom.
    const computeDropdownPosition = React.useCallback(() => {
      const btn = buttonRef.current;
      if (!btn) return null;
      const rect = btn.getBoundingClientRect();
      const estimatedHeight = Math.min(
        320, // matches the dropdown's max-h-80
        filteredOptionsRef.current.length * 40 + (isSearchable ? 48 : 0) + 16
      );
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const openUp = spaceBelow < estimatedHeight && spaceAbove > spaceBelow;
      return openUp
        ? { bottom: window.innerHeight - rect.top, left: rect.left, width: rect.width }
        : { top: rect.bottom, left: rect.left, width: rect.width };
    }, [isSearchable]);

    React.useEffect(() => {
      const handler = setTimeout(() => {
        if (inputValue === query) return;
        if (!open) return;
        setQuery(inputValue);
        onSearch?.(inputValue);
      }, 300);

      return () => clearTimeout(handler);
    }, [inputValue, 300, onSearch]);

    React.useEffect(() => {
      function handleClickOutside(event: MouseEvent) {
        const target = event.target as Node;
        const isInsideContainer = containerRef.current && containerRef.current.contains(target);
        const isInsideDropdown = dropdownRef.current && dropdownRef.current.contains(target);

        if (!isInsideContainer && !isInsideDropdown) {
          commitTypedRef.current();
          isOpen(false);
          setDropdownPosition(null);
        }
      }

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    // Update dropdown position on scroll or resize
    React.useEffect(() => {
      if (!open || !buttonRef.current) return;

      const updatePosition = () => {
        const next = computeDropdownPosition();
        if (next) setDropdownPosition(next);
      };

      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      updatePosition();

      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }, [open, computeDropdownPosition]);
    React.useEffect(() => {
      const node = containerRef.current;
      if (!node) return;

      const handleBlur = (event: FocusEvent) => {
        const next = event.relatedTarget as Node | null;
        // Focus moved on to another control (Tab); a click outside is handled on mousedown.
        if (next && !node.contains(next) && !dropdownRef.current?.contains(next)) {
          commitTypedRef.current();
          isOpen(false);
          setDropdownPosition(null);
        }
        if (node && !node.contains(event.relatedTarget as Node)) {
          onBlur?.(event as unknown as React.FocusEvent<HTMLSelectElement>);
        }
      };

      node.addEventListener('focusout', handleBlur);
      return () => node.removeEventListener('focusout', handleBlur);
    }, [onBlur]);
    React.useEffect(() => {
      if (!autoFocus || disabled) return;
      (isSearchable ? inputRef.current : buttonRef.current)?.focus();
      // Mount only, like the native attribute.
    }, []);
    // Select handling
    const handleSelect = (option: SelectInputOption) => {
      if (isMulti) {
        const exists = selectedValues.find(o => o.value === option.value);

        const newValues = exists
          ? selectedValues.filter(o => o.value !== option.value)
          : [...selectedValues, option];

        setSelectedValues(newValues);
        onChange?.(newValues.map(val => val.value));
      } else {
        setSelectedValues([option]);
        onChange?.(option.value);
      }
    };

    React.useEffect(() => {
      if (isMulti) {
        if (Array.isArray(value)) {
          setSelectedValues(mergedOptions.filter(option => value.includes(option.value)));
        } else {
          setSelectedValues([]);
        }
      } else {
        const selected = Array.isArray(value)
          ? mergedOptions.find(option => value.includes(option.value))
          : (mergedOptions.find(option => option.value === value) ?? customOption(value));

        setSelectedValues(selected ? [selected] : []);
      }
    }, [value, mergedOptions, isMulti, customOption]);

    // --- Keyboard navigation ---------------------------------------------
    // The original implementation used Headless UI's `Listbox` only for state
    // plumbing — the trigger and option markup are custom. As a result, the
    // built-in arrow/Enter/Home/End navigation never wires up, so users
    // couldn't navigate the dropdown with the keyboard. We track the
    // highlighted option ourselves and react to keys on the trigger / search
    // input.

    const findNextEnabled = React.useCallback(
      (options: SelectInputOption[], from: number, direction: 1 | -1): number => {
        const len = options.length;
        if (len === 0) return -1;
        let idx = from;
        for (let i = 0; i < len; i++) {
          idx = (idx + direction + len) % len;
          if (!options[idx]?.disabled) return idx;
        }
        return -1;
      },
      []
    );

    // Search filtering, returns matching on top and non-matching under the former, therefore the select never feels 'empty'
    const searchedOptions = React.useMemo(() => {
      // A caller that filters through onSearch already chose the rows and their order.
      if (!isSearchable || filtersExternally) return mergedOptions;

      const needle = query.toLowerCase();
      const matches = (option: SelectInputOption) =>
        option.label.toLowerCase().includes(needle) ||
        !!option.description?.toLowerCase().includes(needle);
      return [...mergedOptions.filter(matches), ...mergedOptions.filter(o => !matches(o))];
    }, [mergedOptions, query, isSearchable, filtersExternally]);

    // The typed text always heads the list — the option it names, or a row of its own. Built
    // from the undebounced input, so Enter right after typing picks what was typed.
    const typedText = inputValue.trim();
    const filteredOptions = React.useMemo(() => {
      if (!allowCustomValue || !isSearchable || isMulti || typedText === '') {
        return searchedOptions;
      }
      const equal = searchedOptions.find(
        option => option.value === typedText || option.label === typedText
      );
      return [
        equal ?? { value: typedText, label: typedText, description: customValueDescription },
        ...searchedOptions.filter(option => option !== equal)
      ];
    }, [
      allowCustomValue,
      isSearchable,
      isMulti,
      typedText,
      searchedOptions,
      customValueDescription
    ]);

    // Refreshed after every render, so the listeners bound once above see the latest typed text.
    React.useLayoutEffect(() => {
      commitTypedRef.current = () => {
        if (!commitTypedOnBlur || !allowCustomValue || !isSearchable || isMulti) return;
        if (typedText === '') return;
        // The typed text heads the list: the option it names, or a row of its own.
        const typed = filteredOptions[0];
        if (typed && !typed.disabled) handleSelect(typed);
        if (inputRef.current) inputRef.current.value = '';
        setInputValue('');
      };
    });

    // Initialise the highlighted index whenever the dropdown opens — prefer
    // the currently-selected option, otherwise the first enabled row.
    // Reading the latest selection from a ref keeps this effect from
    // re-running on every selection change (e.g. multi-select toggles) which
    // would otherwise wipe out the user's keyboard position.
    const selectedValuesRef = React.useRef(selectedValues);
    React.useEffect(() => {
      selectedValuesRef.current = selectedValues;
    }, [selectedValues]);
    const filteredOptionsRef = React.useRef(filteredOptions);
    React.useEffect(() => {
      filteredOptionsRef.current = filteredOptions;
    }, [filteredOptions]);

    // Which end an opening without a selection starts from: ArrowUp opens on the last option.
    const openDirectionRef = React.useRef<1 | -1>(1);
    React.useEffect(() => {
      if (!open) {
        setHighlightedIndex(-1);
        return;
      }
      const direction = openDirectionRef.current;
      openDirectionRef.current = 1;
      const opts = filteredOptionsRef.current;
      const selectedIdx = opts.findIndex(o =>
        selectedValuesRef.current.some(s => s.value === o.value)
      );
      if (selectedIdx >= 0 && !opts[selectedIdx]?.disabled) {
        setHighlightedIndex(selectedIdx);
      } else {
        setHighlightedIndex(findNextEnabled(opts, direction === 1 ? -1 : 0, direction));
      }
    }, [open, findNextEnabled]);

    // Keep the highlight in bounds while the filtered list changes (search).
    React.useEffect(() => {
      if (!open) return;
      setHighlightedIndex(prev => {
        if (filteredOptions.length === 0) return -1;
        if (prev < 0 || prev >= filteredOptions.length || filteredOptions[prev]?.disabled) {
          return findNextEnabled(filteredOptions, -1, 1);
        }
        return prev;
      });
    }, [filteredOptions, open, findNextEnabled]);

    // Typing moves the highlight back to the typed text, which heads the list.
    React.useEffect(() => {
      if (allowCustomValue && open && typedText !== '') setHighlightedIndex(0);
    }, [allowCustomValue, open, typedText]);

    // Scroll the highlighted option into view when it changes.
    React.useEffect(() => {
      if (!open) return;
      const el = highlightedOptionRef.current;
      if (el) el.scrollIntoView({ block: 'nearest' });
    }, [highlightedIndex, open]);

    const openDropdown = React.useCallback(
      (direction: 1 | -1 = 1) => {
        const next = computeDropdownPosition();
        if (!next) return;
        openDirectionRef.current = direction;
        setDropdownPosition(next);
        isOpen(true);
      },
      [computeDropdownPosition]
    );

    const closeDropdown = React.useCallback(() => {
      isOpen(false);
      setDropdownPosition(null);
    }, []);

    const commitHighlighted = React.useCallback(() => {
      const opt = filteredOptions[highlightedIndex];
      if (!opt || opt.disabled) return;
      handleSelect(opt);
      if (isSearchable) {
        if (inputRef.current) inputRef.current.value = '';
        setInputValue('');
      }
      if (!isMulti) closeDropdown();
    }, [
      filteredOptions,
      highlightedIndex,
      isMulti,
      isSearchable,
      closeDropdown
      // handleSelect intentionally omitted — it's a stable closure over state setters
    ]);

    // Shared keyboard handler used by both the trigger button and the search
    // input. Returns true when the event was handled (caller can stop other
    // logic from running).
    const handleNavKey = React.useCallback(
      (event: React.KeyboardEvent): boolean => {
        if (disabled) return false;
        switch (event.key) {
          case 'ArrowDown':
            event.preventDefault();
            event.stopPropagation();
            if (!open) {
              openDropdown(1);
            } else {
              setHighlightedIndex(prev => findNextEnabled(filteredOptions, prev, 1));
            }
            return true;
          case 'ArrowUp':
            event.preventDefault();
            event.stopPropagation();
            if (!open) {
              openDropdown(-1);
            } else {
              setHighlightedIndex(prev => findNextEnabled(filteredOptions, prev, -1));
            }
            return true;
          case 'Home':
            if (!open) return false;
            event.preventDefault();
            event.stopPropagation();
            setHighlightedIndex(findNextEnabled(filteredOptions, -1, 1));
            return true;
          case 'End':
            if (!open) return false;
            event.preventDefault();
            event.stopPropagation();
            setHighlightedIndex(findNextEnabled(filteredOptions, 0, -1));
            return true;
          case 'Enter':
            if (!open) {
              event.preventDefault();
              openDropdown();
              return true;
            }
            event.preventDefault();
            event.stopPropagation();
            commitHighlighted();
            return true;
          case 'Escape':
            if (!open) return false;
            event.preventDefault();
            event.stopPropagation();
            closeDropdown();
            return true;
          default:
            return false;
        }
      },
      [
        disabled,
        open,
        filteredOptions,
        openDropdown,
        closeDropdown,
        commitHighlighted,
        findNextEnabled
      ]
    );

    //Highlighting matching parts of the search query, highlighting has a text shadow
    const highlightMatch = (label: string, query: string) => {
      if (!query) return label;

      // Escaped: the query is typed text, and `(` or `+` must match literally, not throw.
      const regex = new RegExp(`(${escapeRegExp(query)})`, 'i');
      const parts = label.split(regex);

      return parts.map((part, i) =>
        regex.test(part) ? (
          <span
            key={i}
            className='text-primary-base'
            style={{ textShadow: '0 0 1px currentColor, 0 0 5px currentColor' }}
          >
            {part}
          </span>
        ) : (
          part
        )
      );
    };

    const renderOptionText = (option: SelectInputOption, labelClassName: string) => (
      <span className='flex flex-col min-w-0'>
        <span className={labelClassName}>{highlightMatch(option.label, query)}</span>
        {option.description && (
          <span className='select-none text-sm text-foreground-muted'>
            {highlightMatch(option.description, query)}
          </span>
        )}
      </span>
    );

    //Removes the selected value when using multi-select
    const RemoveSelectedItem = (value: string | number) => {
      const newValues = selectedValues.filter(o => o.value !== value);
      setSelectedValues(newValues);
      onChange?.(newValues.map(val => val.value));
    };

    //dropdown size - use full width of the trigger button
    const DropdownSize = 'w-full';

    // The focused control points at the highlighted option, so focus never leaves it.
    const listboxId = React.useId();
    const optionId = (idx: number) => `${listboxId}-option-${idx}`;
    const menuShown = !disabled && open && !!dropdownPosition;
    // Combobox semantics go on the element with focus: the trigger, or the search input.
    const comboboxAria = {
      role: 'combobox',
      'aria-label': ariaLabel,
      'aria-describedby': ariaDescribedBy || undefined,
      'aria-invalid': hasError || undefined,
      'aria-expanded': open,
      'aria-haspopup': 'listbox' as const,
      'aria-controls': menuShown ? listboxId : undefined,
      'aria-activedescendant':
        menuShown && filteredOptions[highlightedIndex] ? optionId(highlightedIndex) : undefined
    };

    return (
      <div ref={containerRef} className='w-full'>
        <Field className={cn('flex flex-col gap-sm w-full')}>
          <Listbox as='div' ref={ref} {...props} className='relative w-full'>
            <div
              ref={buttonRef}
              // Searchable: the search input inside is the one tab stop and the combobox.
              tabIndex={disabled || isSearchable ? -1 : 0}
              data-testid={getTestId({ testId, id, name })}
              {...(isSearchable ? {} : { ...comboboxAria, 'aria-disabled': disabled || undefined })}
              onClick={() => {
                if (disabled) return;
                if (!open) {
                  onBlur?.({} as unknown as React.FocusEvent<HTMLSelectElement>);
                  const next = computeDropdownPosition();
                  if (next) setDropdownPosition(next);
                } else {
                  setDropdownPosition(null);
                }
                isOpen(prev => !prev);
              }}
              onKeyDown={e => {
                if (disabled) return;
                // Searchable selects route keys through the inner <input> so
                // the trigger only handles keys when the input isn't focused.
                if (isSearchable && document.activeElement === inputRef.current) return;
                if (handleNavKey(e)) return;
                // Space toggles for non-searchable selects when closed —
                // matches the previous behaviour for opening via keyboard.
                if (e.key === ' ' && !open) {
                  e.preventDefault();
                  openDropdown();
                }
              }}
              className={cn(
                SelectInputVariants({ variant, hasError }),
                GLOBAL_HEIGHT[height],
                className,
                'inline-flex gap-xs relative w-full',
                'focus-visible:ring-4 focus-visible:ring-border-base focus-visible:border-primary-base!',
                // Searchable: focus sits in the search input, so the field shows it like an Input.
                'has-[input:focus]:ring-4 has-[input:focus]:ring-border-base has-[input:focus]:border-primary-base!',
                open && 'ring-4 ring-border-base border-primary-base! pr-lg',
                open && hasError && 'ring-4 ring-error-bg border-error-text!',
                isMulti && 'flex-wrap pr-xl'
              )}
            >
              {/* Shows the selected icon in the case of a non-search non-multi select*/}
              {(() => {
                const iconName = !isMulti
                  ? !isSearchable
                    ? selectedValues?.[0]?.icon || buttonIcon
                    : buttonIcon
                  : buttonIcon;
                return iconName ? <Icon name={iconName} /> : null;
              })()}

              {/* Renders small tags in case of multi or searchable select, multi selects also have a remove button */}
              {isMulti ? (
                selectedValues.map(item => (
                  <span
                    key={item.value}
                    className='group relative inline-flex items-center cursor-default bg-background-muted text-primary-base rounded-lg px-sm py-2xs text-sm select-none transition-all duration-300'
                  >
                    {item.icon && <Icon name={item.icon} className='mr-xs'></Icon>}
                    <span>{item.label}</span>

                    {/* Close icon container */}
                    <span className='flex items-center overflow-hidden w-4 ml-xs'>
                      <Icon
                        name='xmark'
                        className='h-4 w-4 cursor-pointer text-primary-base opacity-0 group-hover:opacity-100 transition-opacity duration-300'
                        onClick={e => {
                          e.stopPropagation();
                          RemoveSelectedItem(item.value);
                        }}
                      />
                    </span>
                  </span>
                ))
              ) : isSearchable &&
                selectedValues.length > 0 &&
                !inputValue &&
                selectedValues[0]?.icon ? (
                <Icon name={selectedValues[0]?.icon}></Icon>
              ) : (
                ''
              )}

              {/* Shows either the selected value or an input if searchable, the selected values are then moved into tags */}
              {isSearchable ? (
                <input
                  ref={inputRef}
                  // The search input is the control a `<label htmlFor>` names.
                  id={id}
                  type='text'
                  {...comboboxAria}
                  aria-autocomplete='list'
                  disabled={disabled}
                  value={inputValue}
                  onChange={e => setInputValue(e.target.value)}
                  onKeyDown={handleNavKey}
                  onClick={e => {
                    e.stopPropagation();
                    isOpen(true);
                    if (
                      filteredOptions.length === 0 ||
                      (filteredOptions.length === 1 && filteredOptions[0]?.value === '')
                    ) {
                      onSearch?.(inputValue);
                    }
                  }}
                  className={cn(
                    'outline-none text-md font-normal h-7 overflow-visible border-transparent bg-transparent focus:ring-transparent focus:outline-none focus:border-transparent',
                    selectedValues[0]?.label === UnselectedOption ||
                      (selectedValues[0]?.label === undefined
                        ? 'placeholder:text-foreground-disabled'
                        : 'placeholder:text-foreground-base'),
                    // Multi: the input shares the row with the tags instead of wrapping below them.
                    isMulti ? 'min-w-12 flex-1' : 'w-full'
                  )}
                  placeholder={
                    selectedValues.length === 0 && !inputValue
                      ? buttonLabel
                      : isMulti
                        ? ''
                        : selectedValues[0]?.label
                  }
                />
              ) : (
                <span
                  className={cn(
                    'font-normal select-none truncate min-w-0 flex-1 pr-md',
                    !isMulti
                      ? selectedValues[0]?.label === UnselectedOption
                        ? 'text-foreground-muted'
                        : 'text-foreground-base'
                      : 'text-foreground-muted'
                  )}
                >
                  {!isMulti
                    ? selectedValues[0]?.label || buttonLabel
                    : selectedValues.length === 0
                      ? buttonLabel
                      : ''}
                </span>
              )}

              {/* select arrow */}
              {!disabled && (
                <Icon
                  name={isSearchable && !hidesSearchIcon ? 'magnifying-glass' : 'chevron-down'}
                  className={cn(
                    'absolute right-4 transition duration-300 ease-out',
                    open && (!isSearchable || hidesSearchIcon) && 'rotate-180'
                  )}
                />
              )}
            </div>
            {/* Headless UI's Portal rather than createPortal: it registers with the surrounding
                portal group, so a Select nested in a Popover or Dialog panel does not read as an
                outside click when an option is picked. */}
            {menuShown && (
              <Portal>
                <Transition
                  show={open}
                  enter='transition duration-100 ease-out'
                  enterFrom='opacity-0 scale-95 -translate-y-2'
                  enterTo='opacity-100 scale-100 translate-y-0'
                  leave='transition duration-100 ease-in'
                  leaveFrom='opacity-100 scale-100 translate-y-0'
                  leaveTo='opacity-0 scale-95 -translate-y-2  '
                >
                  <div
                    ref={dropdownRef}
                    id={listboxId}
                    role='listbox'
                    // A click on an option must not take focus from the control (options are focusable).
                    onMouseDown={e => e.preventDefault()}
                    className={cn(
                      // `w-max`: the list is as wide as its longest option, never the whole
                      // trigger; the trigger's width caps it so a long label wraps instead.
                      'bg-background-input border border-border-base! rounded-lg shadow-lg fixed max-h-80 overflow-y-auto z-110 min-w-48 w-max'
                    )}
                    style={{
                      ...(dropdownPosition.top !== undefined
                        ? { top: `${dropdownPosition.top}px` }
                        : {}),
                      ...(dropdownPosition.bottom !== undefined
                        ? { bottom: `${dropdownPosition.bottom}px` }
                        : {}),
                      left: `${dropdownPosition.left}px`,
                      maxWidth: `${dropdownPosition.width}px`
                    }}
                  >
                    {/* Renders multi or single options */}
                    {(() => {
                      // Index of the first non-preferred row in the filtered
                      // list — used to draw a divider between the preferred
                      // group and the rest. -1 when one of the groups is empty.
                      const firstNonPreferredIdx = (() => {
                        const hasPreferred = filteredOptions.some(o => o.preferred);
                        const hasOther = filteredOptions.some(o => !o.preferred && o.value !== '');
                        if (!hasPreferred || !hasOther) return -1;
                        return filteredOptions.findIndex(o => !o.preferred && o.value !== '');
                      })();
                      return filteredOptions.map((option, idx) => {
                        const isSelected = selectedValues.find(o => o.value === option.value);
                        const isHighlighted = idx === highlightedIndex;
                        const showDivider = idx === firstNonPreferredIdx;
                        const divider = showDivider ? (
                          <div
                            key={`divider-${idx}`}
                            role='separator'
                            aria-hidden='true'
                            className='border-t-2 border-border-base my-xs mx-sm'
                          />
                        ) : null;
                        const attachHighlightRef = (el: HTMLElement | null) => {
                          if (isHighlighted) highlightedOptionRef.current = el;
                        };

                        const row = isMulti ? (
                          <div
                            key={option.value}
                            id={optionId(idx)}
                            ref={attachHighlightRef}
                            role='option'
                            aria-selected={!!isSelected}
                            onClick={e => {
                              if (option.disabled) return;
                              e.stopPropagation();
                              if (isSearchable) {
                                inputRef.current!.value = '';
                                setInputValue('');
                              }
                              handleSelect(option);
                            }}
                            className={cn(
                              DropdownSize,
                              // `min-h-11`, never `h-11`: a long label wraps, and a fixed height
                              // makes the extra lines overflow onto the row below.
                              'flex items-center gap-sm min-h-11 p-sm transition duration-300 cursor-pointer',
                              option.disabled
                                ? 'opacity-50 cursor-not-allowed'
                                : 'hover:bg-background-muted',
                              isHighlighted && !option.disabled && 'bg-background-muted'
                            )}
                          >
                            {option.icon && <Icon name={option.icon} />}
                            {renderOptionText(option, 'select-none')}
                            {/* Always in the row: the list keeps its width when a row is picked. */}
                            <Icon
                              name='check'
                              className={cn(
                                'ml-auto text-success-text',
                                !isSelected && 'invisible'
                              )}
                            />
                          </div>
                        ) : (
                          <ListboxOption
                            disabled={option.disabled || false}
                            key={option.value}
                            id={optionId(idx)}
                            value={option}
                            ref={attachHighlightRef}
                            onClick={() => {
                              handleSelect(option);
                              if (isSearchable) {
                                inputRef.current!.value = '';
                                setInputValue('');
                              }
                              isOpen(false);
                            }}
                            className={cn(
                              // `min-h-11`, never `h-11`: see the multi-select row above.
                              'flex items-center gap-sm min-h-11 p-sm transition duration-300',
                              option.disabled
                                ? 'opacity-50 cursor-not-allowed'
                                : 'hover:bg-background-muted',
                              isHighlighted && !option.disabled && 'bg-background-muted',
                              DropdownSize
                            )}
                          >
                            {option.icon && <Icon name={option.icon} />}
                            {renderOptionText(option, 'select-none font-normal')}
                          </ListboxOption>
                        );

                        return showDivider
                          ? [divider, React.cloneElement(row, { key: `row-${option.value}` })]
                          : row;
                      });
                    })()}
                  </div>
                </Transition>
              </Portal>
            )}
          </Listbox>
        </Field>
      </div>
    );
  }
);

Select.displayName = 'Select';
